/**
 * PaymentLinkFormDialog - Wizard premium para criar/editar link de pagamento
 * 
 * Layout com sidebar escura à esquerda (steps + resumo) e conteúdo à direita.
 * Segue o padrão visual de ImportListWizard e TemplateWizardSidebar.
 * 
 * Steps:
 * 0. Dados básicos (nome, descrição, método, tipo)
 * 1. Itens da Cobrança (produtos/serviços do catálogo — obrigatório)
 * 2. Meios de Pagamento (vincular meios do sistema aos billing_types)
 * 3. Configurações (data final, parcelas, boleto, notificações, endereço)
 * 4. Callback (URL de sucesso, auto-redirect)
 * 5. Imagens (upload, preview, definir principal — apenas em edição)
 * 6. Revisão (resumo antes de salvar)
 * 
 * RELEASE: 2026-03-21 v1.0.0 - Criação do componente
 * RELEASE: 2026-03-21 v1.1.0 - Adição do step de Imagens
 * RELEASE: 2026-03-21 v2.0.0 - Layout Wizard Premium, ícones SVG, CurrencyInput
 * RELEASE: 2026-03-21 v3.0.0 - Adição do step de Itens (produtos/serviços obrigatórios)
 */

import { useState, useEffect, useRef, useCallback, useMemo, DragEvent } from 'react';
import { type PaymentLink, type PaymentLinkFormData, type PaymentLinkImage } from '@/hooks/useFinancialPaymentLinks';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { useAuth } from '@/hooks/useAuth';
import { useFinanceiroMeiosPagamento } from '@/hooks/useFinanceiroMeiosPagamento';
import { useCrmProducts } from '@/hooks/useCrmProducts';
import { PaymentMethodIcon, DefaultPaymentIcon } from '@/components/icons/PaymentMethodIcons';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PixIcon } from '@/components/icons/PixIcon';
import { BoletoIcon } from '@/components/icons/BoletoIcon';
import {
  Link as LinkIcon,
  Settings,
  Globe,
  ImageIcon,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
  Upload,
  Trash2,
  Star,
  AlertCircle,
  CreditCard,
  Wallet,
  Package,
  ShoppingCart,
  Plus,
  Percent,
  Calculator,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
import { ProductCatalogModal } from '@/components/crm/deals/modal/tabs/products/ProductCatalogModal';
import { InlineEditableField } from '@/components/crm/deals/modal/tabs/products/InlineEditableField';
import { formatCurrency } from '@/lib/currency-utils';

const logger = createLogger('PaymentLinkFormDialog');

// ============= TIPOS E CONSTANTES =============

interface PaymentLinkFormDialogProps {
  open: boolean;
  onClose: () => void;
  editingLink: PaymentLink | null;
  contaCorrenteId: string;
  onCreate: (data: PaymentLinkFormData) => Promise<unknown>;
  onUpdate: (params: { paymentLinkId: string; providerLinkId: string; data: Partial<PaymentLinkFormData> }) => Promise<unknown>;
  isCreating: boolean;
  isUpdating: boolean;
  /** Mutations de imagem — requeridas para step de imagens */
  uploadImage?: (params: { providerLinkId: string; imageData: string; filename: string; main?: boolean }) => Promise<unknown>;
  deleteImage?: (params: { providerLinkId: string; imageId: string }) => Promise<unknown>;
  setMainImage?: (params: { providerLinkId: string; imageId: string }) => Promise<unknown>;
  isUploadingImage?: boolean;
  isDeletingImage?: boolean;
}

/** Definição de cada step do wizard */
interface StepDef {
  label: string;
  description: string;
  icon: React.ElementType;
}

/** Steps do wizard (com Meios de Pagamento entre Itens e Configurações) */
const STEPS: StepDef[] = [
  { label: 'Dados Básicos', description: 'Nome e método de pagamento', icon: LinkIcon },
  { label: 'Itens', description: 'Produtos e serviços', icon: ShoppingCart },
  { label: 'Meios Pgto', description: 'Vincular meios de pagamento', icon: Wallet },
  { label: 'Configurações', description: 'Parcelas, validade e opções', icon: Settings },
  { label: 'Callback', description: 'URLs de retorno', icon: Globe },
  { label: 'Imagens', description: 'Fotos do produto', icon: ImageIcon },
  { label: 'Revisão', description: 'Confirmar e salvar', icon: CheckCircle2 },
];

/** Labels amigáveis para método de pagamento */
const BILLING_LABELS: Record<string, string> = {
  UNDEFINED: 'Todos os métodos',
  BOLETO: 'Boleto',
  CREDIT_CARD: 'Cartão de Crédito',
  PIX: 'PIX',
};

/** Labels amigáveis para tipo de cobrança */
const CHARGE_LABELS: Record<string, string> = {
  DETACHED: 'Cobrança Avulsa',
  INSTALLMENT: 'Parcelamento',
};

/** Configuração dos cards de método de pagamento */
const BILLING_TYPE_OPTIONS = [
  { value: 'UNDEFINED', label: 'Todos', icon: Package, description: 'PIX, Cartão e Boleto' },
  { value: 'PIX', label: 'PIX', icon: PixIcon, description: 'Pagamento instantâneo' },
  { value: 'CREDIT_CARD', label: 'Cartão', icon: CreditCard, description: 'Crédito' },
  { value: 'BOLETO', label: 'Boleto', icon: BoletoIcon, description: 'Bancário' },
];

/** Item interno do formulário de itens do link */
interface FormItem {
  id: string;
  product_id: string;
  product_name: string;
  product_sku: string | null;
  descricao: string;
  quantidade: number;
  valor_unitario: number;
  desconto: number;
}

// ============= COMPONENTE PRINCIPAL =============

export function PaymentLinkFormDialog({
  open,
  onClose,
  editingLink,
  contaCorrenteId,
  onCreate,
  onUpdate,
  isCreating,
  isUpdating,
  uploadImage,
  deleteImage,
  setMainImage,
  isUploadingImage = false,
  isDeletingImage = false,
}: PaymentLinkFormDialogProps) {
  const [step, setStep] = useState(0);
  const isEditing = !!editingLink;
  const isSaving = isCreating || isUpdating;
  const providerLinkId = editingLink?.provider_link_id || null;
  const { user } = useAuth();

  // Estado do formulário
  const [form, setForm] = useState<PaymentLinkFormData>({
    name: '',
    description: '',
    value: undefined,
    billing_type: 'UNDEFINED',
    charge_type: 'DETACHED',
    max_installment_count: undefined,
    due_date_limit_days: undefined,
    notification_enabled: true,
    is_address_required: false,
    end_date: undefined,
    external_reference: '',
    callback_config: { successUrl: '', autoRedirect: false },
  });

  // Estado de itens (produtos/serviços)
  const [itens, setItens] = useState<FormItem[]>([]);
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [loadingItens, setLoadingItens] = useState(false);

  // Estado de mapeamento billing_type → meio_pagamento_id
  const [meiosMapping, setMeiosMapping] = useState<Record<string, string>>({});
  const [loadingMeiosMapping, setLoadingMeiosMapping] = useState(false);

  // Estado de imagens
  const [images, setImages] = useState<PaymentLinkImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** Estado do lightbox de preview de imagens */
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  /** IDs de imagens que falharam ao carregar */
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // Hook de produtos para catálogo
  const { products } = useCrmProducts();
  // Hook de meios de pagamento do sistema
  const { meios: meiosPagamento } = useFinanceiroMeiosPagamento();

  // ============= CÁLCULOS DE TOTAIS =============

  /** Subtotal = soma de (quantidade * valor_unitario) */
  const subtotalGeral = useMemo(
    () => itens.reduce((sum, item) => sum + item.quantidade * item.valor_unitario, 0),
    [itens]
  );

  /** Total de descontos */
  const totalDescontos = useMemo(
    () => itens.reduce((sum, item) => sum + item.desconto, 0),
    [itens]
  );

  /** Valor total = subtotal - descontos */
  const valorTotal = useMemo(
    () => subtotalGeral - totalDescontos,
    [subtotalGeral, totalDescontos]
  );

  /** Formatar moeda BRL */
  const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  // ============= FUNÇÕES AUXILIARES =============

  /**
   * Resolve URL da imagem — garante URL absoluta
   */
  const resolveImageUrl = useCallback((url: string): string => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://www.asaas.com${url.startsWith('/') ? '' : '/'}${url}`;
  }, []);

  /** Abre lightbox na imagem clicada */
  const handleImageClick = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  /** MediaItems para o lightbox */
  const lightboxMediaItems: MediaItem[] = images.map(img => ({
    url: img.proxiedUrl || resolveImageUrl(img.previewUrl || img.downloadUrl),
    type: 'image' as const,
  }));

  /**
   * Preenche formulário quando editando
   */
  useEffect(() => {
    if (editingLink) {
      setForm({
        name: editingLink.name,
        description: editingLink.description || '',
        value: editingLink.value || undefined,
        billing_type: editingLink.billing_type,
        charge_type: editingLink.charge_type,
        max_installment_count: editingLink.max_installment_count || undefined,
        due_date_limit_days: editingLink.due_date_limit_days || undefined,
        notification_enabled: editingLink.notification_enabled,
        is_address_required: editingLink.is_address_required,
        end_date: editingLink.end_date || undefined,
        external_reference: editingLink.external_reference || '',
        callback_config: editingLink.callback_config || { successUrl: '', autoRedirect: false },
      });
    } else {
      setForm({
        name: '',
        description: '',
        value: undefined,
        billing_type: 'UNDEFINED',
        charge_type: 'DETACHED',
        notification_enabled: true,
        is_address_required: false,
        callback_config: { successUrl: '', autoRedirect: false },
      });
    }
    setStep(0);
    setImages([]);
    setItens([]);
    setMeiosMapping({});
  }, [editingLink, open]);

  /**
   * Carrega itens existentes ao abrir em modo edição
   */
  useEffect(() => {
    if (open && isEditing && editingLink) {
      loadExistingItems(editingLink.id);
      loadExistingMeiosMapping(editingLink.id);
    }
  }, [open, isEditing, editingLink]);

  /**
   * Busca itens do link de pagamento da tabela local
   */
  const loadExistingItems = async (paymentLinkId: string) => {
    setLoadingItens(true);
    try {
      const { data, error } = await supabase
        .from('financeiro_payment_link_itens' as any)
        .select('*, product:crm_products(id, name, sku)')
        .eq('payment_link_id', paymentLinkId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const mapped = ((data || []) as any[]).map((item: any) => ({
        id: item.id,
        product_id: item.product_id || '',
        product_name: item.product?.name || item.descricao,
        product_sku: item.product?.sku || null,
        descricao: item.descricao,
        quantidade: item.quantidade,
        valor_unitario: Number(item.valor_unitario),
        desconto: Number(item.desconto || 0),
      }));
      setItens(mapped);
    } catch (err) {
      logger.error('Erro ao carregar itens do link', err);
    } finally {
      setLoadingItens(false);
    }
  };

  /**
   * Busca mapeamentos de meios de pagamento existentes do link
   */
  const loadExistingMeiosMapping = async (paymentLinkId: string) => {
    setLoadingMeiosMapping(true);
    try {
      const { data, error } = await supabase
        .from('financeiro_payment_link_meios' as any)
        .select('billing_type, meio_pagamento_id')
        .eq('payment_link_id', paymentLinkId);

      if (error) throw error;

      const mapping: Record<string, string> = {};
      ((data || []) as any[]).forEach((item: any) => {
        mapping[item.billing_type] = item.meio_pagamento_id;
      });
      setMeiosMapping(mapping);
    } catch (err) {
      logger.error('Erro ao carregar mapeamento de meios', err);
    } finally {
      setLoadingMeiosMapping(false);
    }
  };

  /**
   * Carrega imagens do provider ao abrir step de imagens (modo edição)
   */
  useEffect(() => {
    if (step === 5 && providerLinkId && isEditing) {
      loadImages();
    }
  }, [step, providerLinkId, isEditing]);

  /**
   * Busca imagens do link via edge function
   */
  const loadImages = async () => {
    if (!providerLinkId || !contaCorrenteId) return;
    setLoadingImages(true);
    try {
      const { data, error } = await supabase.functions.invoke('financial-payment-links', {
        body: {
          action: 'list_images',
          provider_link_id: providerLinkId,
          conta_corrente_id: contaCorrenteId,
        },
      });
      if (error) throw error;
      const rawItems = data?.data || [];
      const mapped = rawItems.map((item: any) => ({
        id: item.id,
        main: item.main ?? false,
        proxiedUrl: item.proxiedUrl || '',
        previewUrl: item.previewUrl || '',
        downloadUrl: item.downloadUrl || '',
        originalName: item.originalName || '',
        size: item.size || 0,
        extension: item.extension || '',
      }));
      setImages(mapped);
      setFailedImages(new Set());
    } catch (err) {
      logger.error('Erro ao carregar imagens', err);
    } finally {
      setLoadingImages(false);
    }
  };

  /**
   * Atualiza campo do formulário
   */
  const updateField = <K extends keyof PaymentLinkFormData>(key: K, value: PaymentLinkFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Persiste itens na tabela financeiro_payment_link_itens
   */
  const saveItems = async (paymentLinkId: string, tenantId: string) => {
    // Deletar itens existentes
    await supabase
      .from('financeiro_payment_link_itens' as any)
      .delete()
      .eq('payment_link_id', paymentLinkId);

    // Inserir itens atuais
    if (itens.length > 0) {
      const rows = itens.map(item => ({
        tenant_id: tenantId,
        payment_link_id: paymentLinkId,
        product_id: item.product_id || null,
        descricao: item.descricao,
        quantidade: item.quantidade,
        valor_unitario: item.valor_unitario,
        desconto: item.desconto,
      }));

      const { error } = await supabase
        .from('financeiro_payment_link_itens' as any)
        .insert(rows);

      if (error) {
        logger.error('Erro ao salvar itens do link', error);
        throw error;
      }
    }
  };

  /**
   * Persiste mapeamentos de meios de pagamento na tabela financeiro_payment_link_meios
   */
  const saveMeiosMapping = async (paymentLinkId: string, tenantId: string) => {
    // Deletar mapeamentos existentes
    await supabase
      .from('financeiro_payment_link_meios' as any)
      .delete()
      .eq('payment_link_id', paymentLinkId);

    // Inserir mapeamentos atuais
    const entries = Object.entries(meiosMapping).filter(([, meioPagId]) => !!meioPagId);
    if (entries.length > 0) {
      const rows = entries.map(([billingType, meioPagId]) => ({
        tenant_id: tenantId,
        payment_link_id: paymentLinkId,
        billing_type: billingType,
        meio_pagamento_id: meioPagId,
      }));

      const { error } = await supabase
        .from('financeiro_payment_link_meios' as any)
        .insert(rows);

      if (error) {
        logger.error('Erro ao salvar mapeamento de meios de pagamento', error);
        throw error;
      }
    }
  };

  /**
   * Billing types necessários com base no billing_type selecionado no step 0
   */
  const requiredBillingTypes = useMemo(() => {
    if (form.billing_type === 'UNDEFINED') return ['PIX', 'BOLETO', 'CREDIT_CARD'];
    return [form.billing_type];
  }, [form.billing_type]);

  /**
   * Verifica se todos os meios necessários foram selecionados
   */
  const allMeiosMapped = useMemo(() => {
    return requiredBillingTypes.every(bt => !!meiosMapping[bt]);
  }, [requiredBillingTypes, meiosMapping]);

  /**
   * Submete formulário — calcula valor com base nos itens
   */
  const handleSubmit = async () => {
    try {
      // Valor calculado a partir dos itens
      const calculatedValue = valorTotal > 0 ? valorTotal : undefined;
      const formWithValue = { ...form, value: calculatedValue };

      if (isEditing && editingLink) {
        await onUpdate({
          paymentLinkId: editingLink.id,
          providerLinkId: editingLink.provider_link_id!,
          data: formWithValue,
        });
        // Salvar itens e mapeamento de meios após atualizar link
        await saveItems(editingLink.id, editingLink.tenant_id);
        await saveMeiosMapping(editingLink.id, editingLink.tenant_id);
      } else {
        // Criar link — precisamos pegar o ID depois
        const result = await onCreate(formWithValue) as any;
        // Salvar itens e meios usando o ID do link recém-criado
        const linkId = result?.data?.id;
        const linkTenantId = result?.data?.tenant_id;
        if (linkId && linkTenantId) {
          await saveItems(linkId, linkTenantId);
          await saveMeiosMapping(linkId, linkTenantId);
        }
      }
      onClose();
    } catch {
      // Erro já tratado pelo hook
    }
  };

  /**
   * Verifica se o step atual é válido para avançar
   */
  const canAdvance = () => {
    if (step === 0) return !!form.name?.trim();
    if (step === 1) return itens.length > 0;
    if (step === 2) return allMeiosMapped;
    // Step 3: validação condicional de parcelamento
    if (step === 3 && form.charge_type === 'INSTALLMENT') {
      if (!form.max_installment_count || form.max_installment_count < 1) return false;
      // Dias úteis obrigatório se não for cartão de crédito
      if (form.billing_type !== 'CREDIT_CARD') {
        if (form.due_date_limit_days === undefined || form.due_date_limit_days === null) return false;
      }
    }
    return true;
  };

  /**
   * Verifica se um step foi completado
   */
  const isStepCompleted = (index: number) => {
    if (index === 0) return !!form.name?.trim();
    if (index === 1) return itens.length > 0;
    if (index === 2) return allMeiosMapped;
    // Step 3: validação condicional de parcelamento
    if (index === 3) {
      if (form.charge_type === 'INSTALLMENT') {
        if (!form.max_installment_count || form.max_installment_count < 1) return false;
        if (form.billing_type !== 'CREDIT_CARD' && (form.due_date_limit_days === undefined || form.due_date_limit_days === null)) return false;
      }
      return index < step;
    }
    if (index < step) return true;
    return false;
  };

  // =====================================================
  // HANDLERS DE ITENS (CATÁLOGO)
  // =====================================================

  /** Itens no formato esperado pelo ProductCatalogModal */
  const chargeItemsForModal = useMemo(
    () => itens.map(i => ({ product_id: i.product_id, quantity: i.quantidade })),
    [itens]
  );

  /** Handler: adicionar item vindo do catálogo */
  const handleCatalogAddItem = useCallback(async (productId: string, quantity: number, unitPrice: number) => {
    const existing = itens.find(i => i.product_id === productId);
    if (existing) {
      setItens(prev =>
        prev.map(i => i.product_id === productId ? { ...i, quantidade: i.quantidade + quantity } : i)
      );
      return;
    }
    const product = products.find(p => p.id === productId);
    const novoItem: FormItem = {
      id: crypto.randomUUID(),
      product_id: productId,
      product_name: product?.name || 'Produto',
      product_sku: product?.sku || null,
      descricao: product?.name || 'Produto',
      quantidade: quantity,
      valor_unitario: unitPrice,
      desconto: 0,
    };
    setItens(prev => [...prev, novoItem]);
  }, [itens, products]);

  /** Handler: atualizar quantidade vindo do catálogo */
  const handleCatalogUpdateQuantity = useCallback(async (productId: string, quantity: number) => {
    setItens(prev =>
      prev.map(i => i.product_id === productId ? { ...i, quantidade: quantity } : i)
    );
  }, []);

  /** Handler: remover item vindo do catálogo */
  const handleCatalogRemoveItem = useCallback(async (productId: string) => {
    setItens(prev => prev.filter(i => i.product_id !== productId));
  }, []);

  /** Remover item por ID local */
  const handleRemoveItem = useCallback((itemId: string) => {
    setItens(prev => prev.filter(i => i.id !== itemId));
  }, []);

  /** Atualizar campo de um item */
  const handleUpdateItem = useCallback((itemId: string, field: keyof FormItem, value: number | string) => {
    setItens(prev =>
      prev.map(i => i.id === itemId ? { ...i, [field]: value } : i)
    );
  }, []);

  // =====================================================
  // HANDLERS DE IMAGEM
  // =====================================================

  /** Converte arquivo para Base64 e faz upload */
  const handleImageUpload = (file: File) => {
    if (!providerLinkId || !uploadImage) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens são aceitas');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem deve ter no máximo 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = (reader.result as string).split(',')[1];
        await uploadImage({
          providerLinkId,
          imageData: base64,
          filename: file.name,
          main: images.length === 0,
        });
        await loadImages();
      } catch {
        // Erro tratado pelo hook
      }
    };
    reader.readAsDataURL(file);
  };

  /** Handler de drag & drop */
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleImageUpload(files[0]);
  };

  /** Handler de seleção via input */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleImageUpload(files[0]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /** Remover imagem */
  const handleDeleteImage = async (imageId: string) => {
    if (!providerLinkId || !deleteImage) return;
    try {
      await deleteImage({ providerLinkId, imageId });
      await loadImages();
    } catch {
      // Erro tratado pelo hook
    }
  };

  /** Definir imagem principal */
  const handleSetMainImage = async (imageId: string) => {
    if (!providerLinkId || !setMainImage) return;
    try {
      await setMainImage({ providerLinkId, imageId });
      await loadImages();
    } catch {
      // Erro tratado pelo hook
    }
  };

  // =====================================================
  // STEP ICON
  // =====================================================
  const CurrentStepIcon = STEPS[step]?.icon || LinkIcon;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90dvh] h-[85dvh] p-0 gap-0 overflow-hidden min-h-0">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-full min-h-0">

          {/* ==================== SIDEBAR ESQUERDA ==================== */}
          <aside
            className="hidden md:flex flex-col relative overflow-hidden min-h-0"
            style={{
              background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)'
            }}
          >
            {/* Elementos decorativos */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>

            {/* Conteúdo da sidebar */}
            <div className="relative z-10 flex flex-col h-full min-h-0 p-5">
              {/* Header */}
              <div className="mb-6 shrink-0">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <LinkIcon className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">
                  {isEditing ? 'Editar Link' : 'Novo Link'}
                </h2>
                <p className="text-sm text-white/60 mt-1">
                  Etapa {step + 1} de {STEPS.length}
                </p>
              </div>

              {/* Lista de steps */}
              <nav className="flex-1 min-h-0 space-y-1 overflow-y-auto scrollbar-thin">
                {STEPS.map((s, index) => {
                  const completed = isStepCompleted(index);
                  const isActive = index === step;
                  const isPending = !completed && !isActive;
                  const isDisabled = index > step && !completed;
                  const Icon = s.icon;

                  return (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => !isDisabled && setStep(index)}
                      disabled={isDisabled}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all duration-200',
                        isActive && 'bg-white/10 border border-white/20',
                        completed && !isActive && 'hover:bg-white/5 cursor-pointer',
                        isDisabled && 'opacity-50 cursor-not-allowed',
                        !isActive && !isDisabled && 'hover:bg-white/5'
                      )}
                    >
                      {/* Círculo indicador */}
                      <div className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 border-2',
                        completed && 'bg-green-500 border-green-500',
                        isActive && 'bg-white border-white',
                        isPending && 'bg-transparent border-white/30'
                      )}>
                        {completed ? (
                          <Check className="h-4 w-4 text-white" />
                        ) : (
                          <Icon className={cn(
                            'h-4 w-4',
                            isActive ? 'text-primary' : 'text-white/60'
                          )} />
                        )}
                      </div>

                      {/* Texto do step */}
                      <div className="flex-1 min-w-0">
                        <span className={cn(
                          'block text-sm font-medium truncate',
                          isActive ? 'text-white' : 'text-white/80'
                        )}>
                          {s.label}
                        </span>
                        <span className="block text-xs text-white/40 truncate">
                          {s.description}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* Resumo no rodapé */}
              <div className="shrink-0 mt-auto pt-4 border-t border-white/10">
                <h3 className="text-xs font-medium text-white/60 uppercase tracking-wider mb-3">
                  Resumo
                </h3>
                <div className="space-y-2">
                  {/* Nome do link */}
                  {form.name && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <LinkIcon className="h-4 w-4 text-white/60" />
                        <span className="text-sm text-white/80">Nome</span>
                      </div>
                      <span className="text-sm font-medium text-white truncate max-w-[120px]">
                        {form.name}
                      </span>
                    </div>
                  )}

                  {/* Método de pagamento */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Método</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {BILLING_LABELS[form.billing_type] || '—'}
                    </span>
                  </div>

                  {/* Tipo de cobrança */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Tipo</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {CHARGE_LABELS[form.charge_type] || '—'}
                    </span>
                  </div>

                  {/* Valor (calculado dos itens) */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white/60">R$</span>
                      <span className="text-sm text-white/80">Valor</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {itens.length > 0
                        ? formatBRL(valorTotal)
                        : '—'}
                    </span>
                  </div>

                  {/* Qtd itens */}
                  {itens.length > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShoppingCart className="h-4 w-4 text-white/60" />
                        <span className="text-sm text-white/80">Itens</span>
                      </div>
                      <span className="text-sm font-medium text-white">
                        {itens.length}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </aside>

          {/* ==================== CONTEÚDO DIREITO ==================== */}
          <div className="flex flex-col h-full min-h-0 overflow-hidden">

            {/* Header do conteúdo */}
            <div className="shrink-0 px-6 pt-6 pb-4 border-b flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <CurrentStepIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">{STEPS[step].label}</h3>
                <p className="text-sm text-muted-foreground">{STEPS[step].description}</p>
              </div>
              {/* Indicador mobile de steps */}
              <div className="flex gap-1 ml-auto md:hidden">
                {STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      'w-2 h-2 rounded-full transition-colors',
                      i === step ? 'bg-primary' : i < step ? 'bg-primary/40' : 'bg-muted'
                    )}
                  />
                ))}
              </div>
            </div>

            {/* Corpo scrollável */}
            <ScrollArea className="flex-1 min-h-0" axis="y" reserveScrollbarSpace preventScrollChaining>
              <div className="px-6 py-5 pb-10">

              {/* ==================== STEP 0: Dados Básicos ==================== */}
              {step === 0 && (
                <div className="space-y-5">
                  {/* Nome */}
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome do Link *</Label>
                    <Input
                      id="name"
                      placeholder="Ex: Curso de Marketing Digital"
                      value={form.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      maxLength={100}
                    />
                  </div>

                  {/* Descrição */}
                  <div className="space-y-2">
                    <Label htmlFor="description">Descrição</Label>
                    <Textarea
                      id="description"
                      placeholder="Descrição exibida ao pagador..."
                      value={form.description || ''}
                      onChange={(e) => updateField('description', e.target.value)}
                      rows={3}
                    />
                  </div>

                  {/* Método de Pagamento — Cards visuais */}
                  <div className="space-y-2">
                    <Label>Método de Pagamento</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {BILLING_TYPE_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const isSelected = form.billing_type === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => updateField('billing_type', opt.value)}
                            className={cn(
                              'flex items-center gap-3 p-3 rounded-lg border-2 transition-all duration-200 text-left',
                              isSelected
                                ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                                : 'border-border hover:border-primary/40 hover:bg-muted/50'
                            )}
                          >
                            <div className={cn(
                              'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                              isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                            )}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                              <span className={cn(
                                'block text-sm font-medium',
                                isSelected ? 'text-primary' : 'text-foreground'
                              )}>
                                {opt.label}
                              </span>
                              <span className="block text-xs text-muted-foreground">
                                {opt.description}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Tipo de cobrança */}
                  <div className="space-y-2">
                    <Label>Tipo de Cobrança</Label>
                    <Select value={form.charge_type} onValueChange={(v) => updateField('charge_type', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DETACHED">Avulsa</SelectItem>
                        <SelectItem value="INSTALLMENT">Parcelamento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* ==================== STEP 1: Itens da Cobrança ==================== */}
              {step === 1 && (
                <div className="space-y-4">
                  {/* Header com botão adicionar */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">Produtos e Serviços</h3>
                      <span className="text-xs text-destructive">*</span>
                      {itens.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {itens.length} {itens.length === 1 ? 'item' : 'itens'}
                        </Badge>
                      )}
                    </div>
                    {itens.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => setCatalogModalOpen(true)} className="h-7 text-xs">
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Adicionar
                      </Button>
                    )}
                  </div>

                  {/* Lista de itens ou placeholder */}
                  {loadingItens ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-sm text-muted-foreground">Carregando itens...</span>
                    </div>
                  ) : itens.length === 0 ? (
                    <div className="border border-dashed border-border rounded-lg p-8 text-center">
                      <ShoppingCart className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Nenhum produto adicionado
                      </p>
                      <p className="text-xs text-muted-foreground mb-4">
                        Adicione produtos ou serviços do catálogo para compor este link de pagamento
                      </p>
                      <Button variant="outline" onClick={() => setCatalogModalOpen(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar Produto ou Serviço
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Tabela de itens */}
                      <div className="border border-border/50 rounded-lg overflow-hidden">
                        <ScrollArea className="max-h-[300px]">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/30">
                                <TableHead className="text-xs h-8">Produto</TableHead>
                                <TableHead className="text-xs h-8 w-20 text-center">Qtd</TableHead>
                                <TableHead className="text-xs h-8 w-24 text-right">Preço</TableHead>
                                <TableHead className="text-xs h-8 w-24 text-right">Desc.</TableHead>
                                <TableHead className="text-xs h-8 w-24 text-right">Total</TableHead>
                                <TableHead className="text-xs h-8 w-10" />
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {itens.map((item) => {
                                const itemSubtotal = item.quantidade * item.valor_unitario;
                                const itemTotal = itemSubtotal - item.desconto;

                                return (
                                  <TableRow key={item.id} className="group">
                                    {/* Produto */}
                                    <TableCell className="py-2">
                                      <div className="flex items-center gap-2">
                                        <Package className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                                        <div className="min-w-0">
                                          <p className="text-xs font-medium truncate">{item.product_name}</p>
                                          {item.product_sku && (
                                            <p className="text-[10px] text-muted-foreground">{item.product_sku}</p>
                                          )}
                                        </div>
                                      </div>
                                    </TableCell>

                                    {/* Quantidade */}
                                    <TableCell className="py-2">
                                      <InlineEditableField
                                        value={item.quantidade}
                                        onChange={(qty) => handleUpdateItem(item.id, 'quantidade', Math.max(1, qty))}
                                        min={1}
                                        allowDecimals={false}
                                        className="h-7 w-16 text-xs text-center mx-auto"
                                      />
                                    </TableCell>

                                    {/* Preço Unitário */}
                                    <TableCell className="py-2 text-right">
                                      <span className="text-xs">
                                        {formatCurrency(item.valor_unitario)}
                                      </span>
                                    </TableCell>

                                    {/* Desconto (R$) */}
                                    <TableCell className="py-2 text-right">
                                      <InlineEditableField
                                        value={item.desconto}
                                        onChange={(val) => handleUpdateItem(item.id, 'desconto', Math.min(val, itemSubtotal))}
                                        min={0}
                                        max={itemSubtotal}
                                        placeholder="-"
                                        className="h-7 w-16 text-xs text-center ml-auto"
                                      />
                                    </TableCell>

                                    {/* Total */}
                                    <TableCell className="py-2 text-right">
                                      <span className="text-xs font-medium">
                                        {formatCurrency(itemTotal)}
                                      </span>
                                    </TableCell>

                                    {/* Ação remover */}
                                    <TableCell className="py-2">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={() => handleRemoveItem(item.id)}
                                      >
                                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </ScrollArea>
                      </div>

                      {/* Totalizadores — 3 cards */}
                      <div className="flex gap-3">
                        {/* Subtotal */}
                        <div className="flex-1 p-3 rounded-lg border bg-muted/30 border-border/50">
                          <div className="flex items-center gap-2 mb-1">
                            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Subtotal</span>
                          </div>
                          <p className="text-lg font-semibold">{formatBRL(subtotalGeral)}</p>
                        </div>
                        {/* Descontos */}
                        <div className="flex-1 p-3 rounded-lg border bg-orange-500/10 border-orange-500/30">
                          <div className="flex items-center gap-2 mb-1">
                            <Percent className="h-4 w-4 text-orange-500" />
                            <span className="text-xs text-muted-foreground">Descontos</span>
                          </div>
                          <p className="text-lg font-semibold text-orange-600">-{formatBRL(totalDescontos)}</p>
                        </div>
                        {/* Total */}
                        <div className="flex-1 p-3 rounded-lg border bg-primary/10 border-primary/30">
                          <div className="flex items-center gap-2 mb-1">
                            <Calculator className="h-4 w-4 text-primary" />
                            <span className="text-xs text-muted-foreground">Total do Link</span>
                          </div>
                          <p className="text-lg font-semibold text-primary">{formatBRL(valorTotal)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Modal do catálogo de produtos */}
                  <ProductCatalogModal
                    open={catalogModalOpen}
                    onOpenChange={setCatalogModalOpen}
                    dealId=""
                    currency="BRL"
                    dealItems={chargeItemsForModal}
                    onAddItem={handleCatalogAddItem}
                    onUpdateItemQuantity={handleCatalogUpdateQuantity}
                    onRemoveItem={handleCatalogRemoveItem}
                  />
                </div>
              )}

              {/* ==================== STEP 2: Meios de Pagamento ==================== */}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Vincular Meios de Pagamento</h3>
                    <span className="text-xs text-destructive">*</span>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Selecione um meio de pagamento cadastrado no sistema para cada tipo aceito neste link.
                    Isso permite identificar automaticamente a forma de pagamento nas cobranças geradas.
                  </p>

                  {loadingMeiosMapping ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-sm text-muted-foreground">Carregando...</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {requiredBillingTypes.map((billingType) => {
                        /** Mapeamento billing_type → tipo_pagamento para filtrar meios */
                        const billingToTipo: Record<string, string> = {
                          'PIX': 'pix',
                          'BOLETO': 'boleto',
                          'CREDIT_CARD': 'cartao_credito',
                        };
                        const tipoPagamento = billingToTipo[billingType] || 'pix';
                        const label = BILLING_LABELS[billingType] || billingType;

                        // Filtrar meios de pagamento ativos do tipo correto
                        const meiosDisponiveis = meiosPagamento.filter(
                          (m) => m.tipo_pagamento === tipoPagamento && m.is_active
                        );

                        // Ícone do tipo de pagamento
                        const IconComp = PaymentMethodIcon[tipoPagamento] || DefaultPaymentIcon;

                        return (
                          <div
                            key={billingType}
                            className={cn(
                              'p-4 rounded-lg border-2 transition-all',
                              meiosMapping[billingType]
                                ? 'border-primary/30 bg-primary/5'
                                : 'border-border bg-muted/20'
                            )}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                                <IconComp className="h-5 w-5" />
                              </div>
                              <div>
                                <span className="text-sm font-medium text-foreground">{label}</span>
                                <p className="text-xs text-muted-foreground">
                                  {meiosDisponiveis.length === 0
                                    ? 'Nenhum meio cadastrado para este tipo'
                                    : `${meiosDisponiveis.length} meio(s) disponível(is)`}
                                </p>
                              </div>
                            </div>

                            {meiosDisponiveis.length === 0 ? (
                              <div className="flex items-center gap-2 text-xs text-destructive">
                                <AlertCircle className="h-3.5 w-3.5" />
                                <span>Cadastre um meio de pagamento do tipo "{label}" no menu Meios de Pagamento</span>
                              </div>
                            ) : (
                              <Select
                                value={meiosMapping[billingType] || ''}
                                onValueChange={(val) =>
                                  setMeiosMapping((prev) => ({ ...prev, [billingType]: val }))
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione um meio de pagamento..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {meiosDisponiveis.map((meio) => (
                                    <SelectItem key={meio.id} value={meio.id}>
                                      {meio.nome}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ==================== STEP 3: Configurações ==================== */}
              {step === 3 && (
                <div className="space-y-4">
                  {/* Parcelas — só se INSTALLMENT */}
                  {form.charge_type === 'INSTALLMENT' && (
                    <div className="space-y-2">
                      <Label htmlFor="maxInstallment">Máximo de Parcelas <span className="text-destructive">*</span></Label>
                      <Input
                        id="maxInstallment"
                        type="number"
                        min={1}
                        max={21}
                        value={form.max_installment_count ?? ''}
                        onChange={(e) => updateField('max_installment_count', e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="1 a 21"
                      />
                    </div>
                  )}

                  {/* Dias úteis vencimento — oculto se tipo pagamento é cartão */}
                  {form.billing_type !== 'CREDIT_CARD' && (
                    <div className="space-y-2">
                      <Label htmlFor="dueDateLimit">
                        Dias úteis para vencimento do boleto
                        {form.charge_type === 'INSTALLMENT' && <span className="text-destructive"> *</span>}
                      </Label>
                      <Input
                        id="dueDateLimit"
                        type="number"
                        min={0}
                        value={form.due_date_limit_days ?? ''}
                        onChange={(e) => updateField('due_date_limit_days', e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="Ex: 3"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="endDate">Data final de validade</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={form.end_date ?? ''}
                      onChange={(e) => updateField('end_date', e.target.value || undefined)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Opcional — deixe vazio para link sem expiração
                    </p>
                  </div>

                  {/* Campo Referência Externa removido — desnecessário para o usuário */}

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Notificações ao cliente</Label>
                      <p className="text-xs text-muted-foreground">Enviar e-mails de cobrança</p>
                    </div>
                    <Switch
                      checked={form.notification_enabled ?? true}
                      onCheckedChange={(v) => updateField('notification_enabled', v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Endereço obrigatório</Label>
                      <p className="text-xs text-muted-foreground">Exigir preenchimento de endereço</p>
                    </div>
                    <Switch
                      checked={form.is_address_required ?? false}
                      onCheckedChange={(v) => updateField('is_address_required', v)}
                    />
                  </div>
                </div>
              )}

              {/* ==================== STEP 4: Callback ==================== */}
              {step === 4 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="successUrl">URL de Sucesso</Label>
                    <Input
                      id="successUrl"
                      type="url"
                      value={form.callback_config?.successUrl || ''}
                      onChange={(e) =>
                        updateField('callback_config', {
                          ...form.callback_config,
                          successUrl: e.target.value,
                        })
                      }
                      placeholder="https://seusite.com/obrigado"
                    />
                    <p className="text-xs text-muted-foreground">
                      URL para redirecionar após o pagamento bem-sucedido
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Auto-redirect</Label>
                      <p className="text-xs text-muted-foreground">Redirecionar automaticamente após pagamento</p>
                    </div>
                    <Switch
                      checked={form.callback_config?.autoRedirect ?? false}
                      onCheckedChange={(v) =>
                        updateField('callback_config', {
                          ...form.callback_config,
                          autoRedirect: v,
                        })
                      }
                    />
                  </div>
                </div>
              )}

              {/* ==================== STEP 5: Imagens ==================== */}
              {step === 5 && (
                <div className="space-y-4">
                  {!isEditing || !providerLinkId ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-3">
                      <AlertCircle className="h-10 w-10 opacity-40" />
                      <p className="text-sm font-medium text-center">
                        Salve o link primeiro para adicionar imagens
                      </p>
                      <p className="text-xs text-center">
                        O upload de imagens requer que o link já exista no provider Asaas
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Área de upload com drag & drop */}
                      <div
                        className={cn(
                          'border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer',
                          isDragging ? 'border-primary bg-primary/5 scale-[1.01]' : 'border-border hover:border-primary/50',
                          isUploadingImage && 'opacity-50 pointer-events-none'
                        )}
                        onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        onClick={() => !isUploadingImage && fileInputRef.current?.click()}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="hidden"
                          disabled={isUploadingImage}
                        />
                        <div className="flex flex-col items-center gap-2 text-center">
                          <div className={cn(
                            'rounded-full p-3',
                            isDragging ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                          )}>
                            <Upload className={cn('h-5 w-5', isUploadingImage && 'animate-pulse')} />
                          </div>
                          <p className="text-sm font-medium">
                            {isUploadingImage ? 'Enviando...' : isDragging ? 'Solte a imagem aqui' : 'Arraste ou clique para enviar'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            JPG, PNG, WEBP — Máximo 5MB
                          </p>
                        </div>
                      </div>

                      {/* Lista de imagens carregadas */}
                      {loadingImages ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                          <span className="ml-2 text-sm text-muted-foreground">Carregando imagens...</span>
                        </div>
                      ) : images.length === 0 ? (
                        <div className="flex flex-col items-center py-4 text-muted-foreground">
                          <ImageIcon className="h-8 w-8 opacity-30 mb-2" />
                          <p className="text-sm">Nenhuma imagem adicionada</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-3">
                          {images.map((img) => (
                            <div
                              key={img.id}
                              className="group relative rounded-lg border overflow-hidden bg-muted aspect-square"
                            >
                              {failedImages.has(img.id) ? (
                                <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground cursor-pointer"
                                  onClick={() => handleImageClick(images.indexOf(img))}
                                >
                                  <ImageIcon className="h-8 w-8 opacity-30" />
                                  <span className="text-[10px] mt-1">Falha ao carregar</span>
                                </div>
                              ) : (
                                <img
                                  src={img.proxiedUrl || resolveImageUrl(img.previewUrl || img.downloadUrl)}
                                  alt={img.originalName}
                                  className="w-full h-full object-cover cursor-pointer"
                                  onClick={() => handleImageClick(images.indexOf(img))}
                                  onError={() => setFailedImages(prev => new Set(prev).add(img.id))}
                                />
                              )}
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-white hover:bg-white/20"
                                  onClick={(e) => { e.stopPropagation(); handleSetMainImage(img.id); }}
                                  title="Definir como principal"
                                >
                                  <Star className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-white hover:bg-destructive/80"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteImage(img.id); }}
                                  disabled={isDeletingImage}
                                  title="Remover imagem"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                                <p className="text-[10px] text-white truncate">{img.originalName}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Lightbox */}
                      <ImageLightbox
                        open={lightboxOpen}
                        onClose={() => setLightboxOpen(false)}
                        mediaItems={lightboxMediaItems}
                        initialIndex={lightboxIndex}
                      />
                    </>
                  )}
                </div>
              )}

              {/* ==================== STEP 6: Revisão (Premium) ==================== */}
              {step === 6 && (
                <div className="space-y-4">
                  {/* Header visual */}
                  <div className="text-center space-y-2 py-2">
                    <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                      <CheckCircle2 className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">Tudo pronto!</h3>
                    <p className="text-xs text-muted-foreground">
                      {isEditing
                        ? 'Revise os dados abaixo e clique em "Salvar" para confirmar as alterações.'
                        : 'Revise os dados abaixo e clique em "Criar Link" para confirmar.'}
                    </p>
                  </div>

                  {/* Card: Dados do Link */}
                  <div className="bg-muted/30 border border-border/40 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10">
                        <LinkIcon className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                        Dados do Link
                      </span>
                    </div>
                    <div className="space-y-0">
                      <div className="flex justify-between items-center py-2 border-b border-border/20">
                        <span className="text-xs text-muted-foreground">Nome</span>
                        <span className="text-sm font-medium text-foreground">{form.name}</span>
                      </div>
                      {form.description && (
                        <div className="flex justify-between items-center py-2 border-b border-border/20">
                          <span className="text-xs text-muted-foreground">Descrição</span>
                          <span className="text-sm text-foreground text-right max-w-[220px] truncate">{form.description}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center py-2">
                        <span className="text-xs text-muted-foreground">Valor</span>
                        <span className="text-sm font-medium text-foreground">
                          {formatBRL(valorTotal)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card: Itens */}
                  {itens.length > 0 && (
                    <div className="bg-muted/30 border border-border/40 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10">
                          <ShoppingCart className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                          Itens
                        </span>
                        <span className="text-[10px] text-muted-foreground ml-auto">
                          {itens.length} {itens.length === 1 ? 'item' : 'itens'}
                        </span>
                      </div>
                      <div className="space-y-0">
                        {itens.map((item, idx) => {
                          const itemTotal = item.quantidade * item.valor_unitario - item.desconto;
                          return (
                            <div key={item.id} className={cn(
                              'flex justify-between items-center py-2',
                              idx < itens.length - 1 && 'border-b border-border/20'
                            )}>
                              <div className="flex items-center gap-2 min-w-0">
                                <Package className="h-3 w-3 text-amber-500 shrink-0" />
                                <span className="text-xs text-foreground truncate">{item.product_name}</span>
                                <span className="text-[10px] text-muted-foreground">x{item.quantidade}</span>
                              </div>
                              <span className="text-xs font-medium text-foreground shrink-0 ml-2">
                                {formatBRL(itemTotal)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Card: Pagamento */}
                  <div className="bg-muted/30 border border-border/40 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10">
                        <CreditCard className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                        Pagamento
                      </span>
                    </div>
                    <div className="space-y-0">
                      <div className="flex justify-between items-center py-2 border-b border-border/20">
                        <span className="text-xs text-muted-foreground">Método</span>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const opt = BILLING_TYPE_OPTIONS.find(o => o.value === form.billing_type);
                            if (!opt) return null;
                            const BillingIcon = opt.icon;
                            return <BillingIcon className="h-4 w-4 text-muted-foreground" />;
                          })()}
                          <Badge variant="outline" className="text-xs">{BILLING_LABELS[form.billing_type]}</Badge>
                        </div>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-xs text-muted-foreground">Tipo</span>
                        <Badge variant="outline" className="text-xs">{CHARGE_LABELS[form.charge_type]}</Badge>
                      </div>
                      {form.charge_type === 'INSTALLMENT' && form.max_installment_count && (
                        <div className="flex justify-between items-center py-2 border-t border-border/20">
                          <span className="text-xs text-muted-foreground">Máx. Parcelas</span>
                          <span className="text-sm font-medium text-foreground">{form.max_installment_count}x</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card: Configurações */}
                  {(form.end_date || form.callback_config?.successUrl) && (
                    <div className="bg-muted/30 border border-border/40 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10">
                          <Settings className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                          Configurações
                        </span>
                      </div>
                      <div className="space-y-0">
                        {form.end_date && (
                          <div className="flex justify-between items-center py-2 border-b border-border/20 last:border-b-0">
                            <span className="text-xs text-muted-foreground">Validade</span>
                            <span className="text-sm text-foreground">{form.end_date}</span>
                          </div>
                        )}
                        {form.callback_config?.successUrl && (
                          <div className="flex justify-between items-center py-2">
                            <span className="text-xs text-muted-foreground">URL Sucesso</span>
                            <span className="text-xs text-muted-foreground max-w-[200px] truncate font-mono">
                              {form.callback_config.successUrl}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Card: Imagens */}
                  {isEditing && images.length > 0 && (
                    <div className="bg-muted/30 border border-border/40 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10">
                          <ImageIcon className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                          Imagens
                        </span>
                        <span className="text-[10px] text-muted-foreground ml-auto">{images.length} imagem(ns)</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {images.map((img) => (
                          <div key={img.id} className="relative w-10 h-10 rounded-md overflow-hidden border border-border/40 bg-muted/50">
                            <img
                              src={img.proxiedUrl || img.previewUrl || img.downloadUrl}
                              alt={img.originalName}
                              className="w-full h-full object-cover"
                            />
                            {img.main && (
                              <div className="absolute bottom-0 left-0 right-0 bg-primary/80 text-primary-foreground text-[7px] text-center leading-tight py-px">
                                Principal
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Nota de rodapé */}
                  <p className="text-[11px] text-center text-muted-foreground pt-1">
                    {isEditing
                      ? 'As alterações serão salvas imediatamente ao confirmar.'
                      : 'O link será criado como ativo e poderá ser compartilhado imediatamente.'}
                  </p>
                </div>
              )}
              </div>
            </ScrollArea>

            {/* Footer com botões de navegação */}
            <div className="shrink-0 px-6 py-4 border-t flex items-center justify-between bg-background">
              <Button
                variant="outline"
                onClick={() => (step === 0 ? onClose() : setStep(step - 1))}
                disabled={isSaving}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                {step === 0 ? 'Cancelar' : 'Voltar'}
              </Button>

              {step < STEPS.length - 1 ? (
                <Button onClick={() => setStep(step + 1)} disabled={!canAdvance()}>
                  Próximo
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={isSaving || !canAdvance()}>
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      {isEditing ? 'Salvar Alterações' : 'Criar Link'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
